import React from 'react'; // eslint-disable-line

import { Grid, withStyles } from '@material-ui/core';
import { capitalCase } from 'change-case';
import { connect, MapStateToProps } from 'react-redux';
import { v1 as uuid } from 'uuid';
import { AuthState } from '../../../../auth/auth-reducer';
import { AddFormModal } from '../../../../form-modal/views/add-form-modal';
import {
    Condition,
    FormHeading,
    FullWidthCheckBoxField,
    FullWidthRichTextField,
    FullWidthSelectField,
    FullWidthTextField,
    True,
    When,
    WhenFieldChanges,
} from '../../../../forms';
import { requiredValidator } from '../../../../forms/validations';
import { State } from '../../../../redux/root-reducer';
import { getDisplayDate, convertToNumber, formatCurrency } from '../../../../shared-functions';
import styles, { StyledComponent } from '../../../../styles/styles';
import {
    AddDeathAssessmentDecision,
    addDeathAssessmentDecisionAction,
} from '../actions/add-death-assessment-decision';
import { ClaimType } from '../../../../claim/claim-types';
import { getClaim } from '../../../../claim';
import { Nullable } from '../../../../shared-types';
import { DeathAssessmentDecisionViewModel } from '../death-assessment-details-reducer';
import { LabeledValue } from '../../../../controls/labeled-value';
import { CurrencyMask } from '../../../../forms/input-masks';
import { getPolicyAge } from '../../../synopsis/death/policy-details/death-benefit-details';

export interface AddDeathAssessmentDecisionProps {
    policyNumber: string;
    dateOfDeath?: Date;
    benefitName: string;
    commencementDate?: Date;
    claimAmount?: number;
    accountBalance?: number;
    assessmentDescription: string;
}

interface StateProps {
    currentUser: AuthState;
    claimType: Nullable<ClaimType>;
}

interface DispatchProps {
    addDeathAssessmentDecision: AddDeathAssessmentDecision;
}

interface DecisionProps extends AddDeathAssessmentDecisionProps, StyledComponent {
    currentClaimType?: string;
}

type Props = DecisionProps & StateProps & DispatchProps & StyledComponent;
const DeathAssessmentDecisionAddFormModal = AddFormModal<DeathAssessmentDecisionViewModel>();
const riskAssessmentDecisionAddView: React.FunctionComponent<Props> = ({
    claimType,
    classes,
    currentUser,
    addDeathAssessmentDecision,
    ...detailProps
}: Props) => {
    const currentClaimType = `${claimType} - ${capitalCase(detailProps.benefitName)}`;
    return (
        <DeathAssessmentDecisionAddFormModal
            recordDetailView={() => (
                <DeathClaimAssessmentDecisionDetailView
                    {...detailProps}
                    currentClaimType={currentClaimType}
                    classes={classes}
                />
            )}
            addRecord={record => addDeathAssessmentDecision(record)}
            blankRecord={getBlankAssessmentDecision()}
        />
    );

    function getBlankAssessmentDecision(): DeathAssessmentDecisionViewModel {
        return {
            ...detailProps,
            id: uuid(),
            benefitName: detailProps.benefitName,
            effectiveDate: detailProps.dateOfDeath,
            amountPayable: detailProps.claimAmount,
            assessmentDate: new Date(),
            assessmentType: detailProps.assessmentDescription,
            assessor: currentUser.name,
            claimType: claimType,
            automaticBenefitIncrease: 0,
            paymentBasis: '',
        };
    }
};

const DeathClaimAssessmentDecisionDetailView: React.FunctionComponent<DecisionProps> = ({
    accountBalance,
    assessmentDescription,
    benefitName,
    claimAmount,
    classes,
    commencementDate,
    currentClaimType,
    dateOfDeath,
    policyNumber,
}: DecisionProps) => {
    return (
        <React.Fragment>
            <FormHeading>Add {assessmentDescription} Decision</FormHeading>
            <Grid container spacing={5} justify="flex-start" className={classes.marginTop}>
                <Grid item xs={3}>
                    <LabeledValue label="Policy number" value={policyNumber} />
                </Grid>
                <Grid item xs={3}>
                    <LabeledValue label="Benefit name" value={benefitName} />
                </Grid>
                <Grid item xs={3}>
                    <LabeledValue
                        label="Commencement date"
                        value={getDisplayDate(commencementDate)}
                    />
                </Grid>
                <Grid item xs={6} lg={3}>
                    <LabeledValue label="Policy Age" value={getPolicyAge(commencementDate)} />
                </Grid>
                <Grid item xs={3}>
                    <LabeledValue label="Claim type" value={currentClaimType} />
                </Grid>
                <Grid item xs={3}>
                    <LabeledValue label="Claim amount" value={formatCurrency(claimAmount)} />
                </Grid>
                <Grid item xs={3}>
                    <LabeledValue label="Account balance" value={formatCurrency(accountBalance)} />
                </Grid>
                <Grid item xs={3}>
                    <LabeledValue label="Effective date" value={getDisplayDate(dateOfDeath)} />
                </Grid>
            </Grid>

            <Grid container spacing={5}>
                <Grid item xs={12}>
                    <FullWidthRichTextField name="summary" validate={requiredValidator} />
                </Grid>
                <Grid item xs={12}>
                    <FullWidthCheckBoxField name="final" label="Final Decision" />
                    <WhenFieldChanges field="final" set="decision" to={null} />
                    <WhenFieldChanges field="final" set="reason" to={null} />
                </Grid>
                <Condition when="final" is={true}>
                    <True>
                        <Grid item xs={4}>
                            <FullWidthSelectField
                                name="decision"
                                options={[
                                    { value: 'Approve', label: 'Approve' },
                                    { value: 'Decline', label: 'Decline' },
                                ]}
                                sortOptions
                                validate={requiredValidator}
                            />
                            <WhenFieldChanges field="decision" set="reason" to={null} />
                        </Grid>
                        <Grid item xs={4}>
                            <Condition when="decision" matchesChild>
                                <When is="Approve">
                                    <FullWidthSelectField
                                        name="reason"
                                        options={[
                                            { value: 'Approve', label: 'Approve' },
                                            {
                                                value: 'New Terms Accepted',
                                                label: 'New Terms Accepted',
                                            },
                                            {
                                                value: 'Business Decision',
                                                label: 'Business Decision',
                                            },
                                            { value: 'Claim Committee', label: 'Claim Committee' },
                                            { value: 'Complaint', label: 'Complaint' },
                                        ]}
                                        sortOptions
                                        validate={requiredValidator}
                                    />
                                </When>
                                <When is="Decline">
                                    <FullWidthSelectField
                                        name="reason"
                                        options={[
                                            {
                                                value: 'Innocent Non-disclosure (I)',
                                                label: 'Innocent Non-disclosure (I)',
                                            },
                                            {
                                                value: 'Deliberate Non-disclosure (D)',
                                                label: 'Deliberate Non-disclosure (D)',
                                            },
                                            { value: 'Fraud', label: 'Fraud' },
                                            {
                                                value: 'Does not meet the criteria',
                                                label: 'Does not meet the criteria',
                                            },
                                            {
                                                value: 'No benefit exists',
                                                label: 'No benefit exists',
                                            },
                                            {
                                                value: 'Benefits expired',
                                                label: 'Benefits expired',
                                            },
                                            { value: 'Exclusion', label: 'Exclusion' },
                                            {
                                                value: 'Condition not covered',
                                                label: 'Condition not covered',
                                            },
                                            { value: 'Policy inactive', label: 'Policy inactive' },
                                            {
                                                value:
                                                    'New terms offered - does not qualify for a claim',
                                                label:
                                                    'New terms offered - does not qualify for a claim',
                                            },
                                            {
                                                value:
                                                    'New terms offered - does qualify for a claim',
                                                label:
                                                    'New terms offered - does qualify for a claim',
                                            },
                                            {
                                                value: 'Within waiting period',
                                                label: 'Within waiting period',
                                            },
                                        ]}
                                        sortOptions
                                        validate={requiredValidator}
                                    />
                                </When>
                                <When default>
                                    <FullWidthSelectField name="reason" disabled options={[]} />
                                </When>
                            </Condition>
                        </Grid>
                        <Condition when="decision" is="Approve">
                            <True>
                                <Grid item xs={4}>
                                    <FullWidthTextField
                                        name="amountPayable"
                                        mask={CurrencyMask}
                                        validate={requiredValidator}
                                        parse={convertToNumber}
                                    />
                                </Grid>
                            </True>
                        </Condition>
                    </True>
                </Condition>
            </Grid>
        </React.Fragment>
    );
};

const mapStateToProps: MapStateToProps<StateProps, AddDeathAssessmentDecisionProps, State> = (
    state: State
) => ({
    currentUser: state.auth as AuthState,
    claimType: getClaim(state).claimType,
});

const mapDispatchToProps = {
    addDeathAssessmentDecision: addDeathAssessmentDecisionAction,
};

export const RiskAssessmentDecisionAddView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(riskAssessmentDecisionAddView));
